/* eslint-disable @octopusdeploy/custom-portal-rules/no-restricted-imports */
import { Drawer } from "@material-ui/core";
import { IconButton } from "@octopusdeploy/design-system-components";
import { AnalyticLinkLocationProvider } from "@octopusdeploy/portal-analytics";
import { usePage } from "@octopusdeploy/portal-routes";
import { OctoLink } from "@octopusdeploy/utilities";
import classNames from "classnames";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import MediaQuery from "react-responsive";
import { createHtmlPortalNode, InPortal, OutPortal } from "react-reverse-portal";
import { Action, useAnalyticActionDispatch } from "~/analytics/Analytics";
import configurationSelectors from "~/areas/configuration/reducers/selectors";
import drawerActions from "~/components/ContextualHelpLayout/reducers/actions";
import * as drawerSelectors from "~/components/ContextualHelpLayout/reducers/selectors";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { Section } from "~/components/Section/Section";
import { ControlledTabsContainer } from "~/primitiveComponents/navigation/Tabs";
import TabItem from "~/primitiveComponents/navigation/Tabs/TabItem";
import type { GlobalState } from "../../store/reducers";
import ExternalLink from "../Navigation/ExternalLink";
import styles from "./ContextualHelpLayout.module.less";
import { HelpSidebarResources } from "./HelpSidebarResources";
import { PageHelpOverviewView, PageHelpTopicsView } from "./PageHelpRegistry/PageHelpSelector";
export const drawerBreakpointWidth = 810;
export function ContextualHelpLayout(props: React.PropsWithChildren<{}>) {
    const dispatch = useDispatch();
    const toggleDrawer: () => void = React.useCallback(() => dispatch(drawerActions.toggleDrawer()), [dispatch]);
    const isDrawerOpen = useSelector(drawerSelectors.isDrawerOpen);
    const isHelpSidebarEnabled = useSelector(configurationSelectors.createFeatureEnabledSelector((x) => x.isHelpSidebarEnabled));
    const newSidebarIsEnabled = isFeatureToggleEnabled("SidebarImprovementsFeatureToggle");
    const portalNode = React.useMemo(() => createHtmlPortalNode(), []);
    return (<React.Fragment>
            <InPortal node={portalNode}>{props.children}</InPortal>
            <MediaQuery minWidth={drawerBreakpointWidth}>
                {(matches: boolean) => {
            if (matches && isHelpSidebarEnabled) {
                return (<div id="drawerwrapper">
                                <div className={styles.root}>
                                    <main className={classNames(styles.mainContent, {
                        [styles.mainContentShift]: isDrawerOpen,
                        [styles.newStylesMergeWithMainContentClassWhenFeatureToggleIsRemoved]: newSidebarIsEnabled,
                    })}>
                                        <OutPortal node={portalNode}/>
                                    </main>
                                    <ContextualHelpDrawer open={isDrawerOpen} toggleDrawer={toggleDrawer}/>
                                </div>
                            </div>);
            }
            else {
                return <OutPortal node={portalNode}/>;
            }
        }}
            </MediaQuery>
        </React.Fragment>);
}
enum DrawerTabKey {
    Overview = "overview",
    HelpTopics = "helpTopics"
}
interface ContextualHelpSidebarContentProps {
    additionalTabsElement: React.ReactElement | undefined;
}
function ContextualHelpSidebarContent(props: ContextualHelpSidebarContentProps) {
    const page = usePage();
    const dispatchAction = useAnalyticActionDispatch();
    const [tab, setTab] = React.useState<string>(DrawerTabKey.Overview);
    const isSidebarImprovementsFeatureEnabled = isFeatureToggleEnabled("SidebarImprovementsFeatureToggle");
    const customHelpSidebarSupportLink = useSelector((state: GlobalState) => state.configurationArea.features && state.configurationArea.features.helpSidebarSupportLink);
    const customHelpSidebarSupportLinkLabel = useSelector((state: GlobalState) => state.configurationArea.features && state.configurationArea.features.helpSidebarSupportLinkLabel);
    const helpSidebarSupportLink = customHelpSidebarSupportLink || OctoLink.Create("HelpGeneral");
    const getHelpSidebarSupportLinkLabel = () => {
        if (!customHelpSidebarSupportLink) {
            return "Our support team is here to help";
        }
        if (!isSidebarImprovementsFeatureEnabled) {
            return "Get support";
        }
        return customHelpSidebarSupportLinkLabel || "Your organization's internal support";
    };
    const dispatch = useDispatch();
    const toggleDrawer: () => void = React.useCallback(() => dispatch(drawerActions.toggleDrawer()), [dispatch]);
    const newSidebarIsEnabled = isFeatureToggleEnabled("SidebarImprovementsFeatureToggle");
    const changeTab = (newTab: string) => {
        if (newTab === tab)
            return;
        setTab(newTab);
        switch (newTab) {
            case DrawerTabKey.Overview:
                dispatchAction("Sidebar: Click Help Tab", { action: Action.Select, resource: "Help Sidebar Overview" });
                break;
            case DrawerTabKey.HelpTopics:
                dispatchAction("Sidebar: Click Resources Tab", { action: Action.Select, resource: "Help Sidebar Resources" });
                break;
        }
    };
    return (<>
            {newSidebarIsEnabled ? (<>
                    <div className={styles.header}>
                        <h4 className={styles.heading}>Help Sidebar</h4>
                        <IconButton icon="Cancel" onClick={() => toggleDrawer()}/>
                    </div>
                </>) : null}
            <div className={classNames(styles.content, {
            [styles.newStylesMergeWithContentClassWhenFeatureToggleIsRemoved]: newSidebarIsEnabled,
        })}>
                {newSidebarIsEnabled ? (<PageHelpOverviewView pageId={page?.Id}/>) : (<ControlledTabsContainer value={tab} onChange={changeTab} afterTabsElement={props.additionalTabsElement}>
                        <TabItem label="Help" value={DrawerTabKey.Overview}>
                            <Section>
                                <AnalyticLinkLocationProvider location="Help sidebar overview">
                                    <PageHelpOverviewView pageId={page?.Id}/>
                                </AnalyticLinkLocationProvider>
                            </Section>
                        </TabItem>
                        <TabItem label="Resources" value={DrawerTabKey.HelpTopics}>
                            <Section>
                                <AnalyticLinkLocationProvider location="Help sidebar resources">
                                    <PageHelpTopicsView pageId={page?.Id}/>
                                </AnalyticLinkLocationProvider>
                            </Section>
                        </TabItem>
                    </ControlledTabsContainer>)}
            </div>
            {newSidebarIsEnabled ? (<HelpSidebarResources />) : (<AnalyticLinkLocationProvider location="Help sidebar footer">
                    <ExternalLink href={helpSidebarSupportLink ? helpSidebarSupportLink : OctoLink.Create("HelpGeneral")} showIcon={false}>
                        <div className={styles.footer}>
                            <h4>Having issues?</h4>
                            <span>
                                {getHelpSidebarSupportLinkLabel()}
                                <em style={{ marginLeft: "0.25rem" }} className="fa-solid fa-external-link" aria-hidden="true"/>
                            </span>
                        </div>
                    </ExternalLink>
                </AnalyticLinkLocationProvider>)}
        </>);
}
interface ContextualHelpDrawerProps {
    open: boolean;
    toggleDrawer: () => void;
}
function ContextualHelpDrawer({ open, toggleDrawer }: ContextualHelpDrawerProps) {
    const [height, setHeight] = React.useState(0);
    const drawerDiv = React.useRef<HTMLDivElement>(null);
    const newSidebarIsEnabled = isFeatureToggleEnabled("SidebarImprovementsFeatureToggle");
    const calculateHeight = React.useCallback(() => {
        if (drawerDiv.current !== null) {
            const top = drawerDiv.current.getBoundingClientRect().top;
            const approxHeightOfPaddingAndOtherJunk = 0;
            setHeight(window.innerHeight - top - approxHeightOfPaddingAndOtherJunk);
        }
    }, []);
    //The material drawer in this layout uses a static position and a height of 100% shifts things like footers off screen
    //we therefore calculate the necessary height and set it
    React.useLayoutEffect(calculateHeight, [calculateHeight]);
    //Since we are manually handling the height, we also need to adjust things based on when the browser resizes
    React.useEffect(() => {
        window.addEventListener("resize", calculateHeight);
        return () => window.removeEventListener("resize", calculateHeight);
    }, [calculateHeight]);
    return (<Drawer className={classNames(styles.drawer, {
            [styles.drawerHidden]: !open,
            [styles.newStylesMergeWithDrawerClassWhenFeatureToggleIsRemoved]: newSidebarIsEnabled,
        })} variant="persistent" anchor="right" open={open} classes={{
            paper: classNames(styles.drawerPaper, {
                [styles.newStylesMergeWithDrawerPaperClassWhenFeatureToggleIsRemoved]: newSidebarIsEnabled,
            }),
        }}>
            <div className={styles.container} ref={drawerDiv} style={{ height }}>
                {open && <ContextualHelpSidebarContent additionalTabsElement={<OverflowMenu menuItems={[OverflowMenuItems.item("Close", toggleDrawer)]} tabIndex={-1}/>}/>}
            </div>
        </Drawer>);
}
